import "../../components/EventUserDashboard.css";
import AdminBackBar from "../../components/admin/AdminBackBar";
import Billede1 from "../../Images/Billede1.jpg";
import HeroSectionV2 from "../../components/HeroSectionV2";
import React, { useEffect, useMemo, useState } from "react";
import {
    Select,
    Paper,
    Title,
    Text,
    Stack,
    Grid,
    Group,
    Button,
    TextInput,
    NumberInput,
    Table,
    Badge,
    Alert,
    Divider,
} from "@mantine/core";

type EventDto = {
    eventID: number;
    eventName?: string;
};

type EventBar = {
    eventBarId: number;
    name: string;
    isActive: boolean;
};

type InvoiceDto = {
    invoiceId: number;
    name: string;
    isActive: boolean;
};

type DrinkTicketCollectionDto = {
    drinkTicketCollectionId: number;
    collectionName: string;
    eventBarId: number;
    maxDrinksPerTicket: number;
    ticketCount: number;
    validDate?: string | null;
};

type DrinkTicketDto = {
    drinkTicketId: number;
    ticketCode: string;
    ticketNumber: number;
    maxDrinks: number;
    isActive: boolean;
};

const toArray = <T,>(r: any): T[] =>
    Array.isArray(r)
        ? r
        : Array.isArray(r?.data)
            ? r.data
            : Array.isArray(r?.items)
                ? r.items
                : Array.isArray(r?.results)
                    ? r.results
                    : [];

const toInt = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const formatDate = (v?: string | null) => {
    if (!v) return "Alle dage";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString("da-DK");
};

const AdminDrinkTicketCollections: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [bars, setBars] = useState<EventBar[]>([]);
    const [eventBarId, setEventBarId] = useState<number>(0);
    const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
    const [invoiceId, setInvoiceId] = useState<number>(0);
    const [collectionName, setCollectionName] = useState("");
    const [ticketCount, setTicketCount] = useState(20);
    const [maxDrinksPerTicket, setMaxDrinksPerTicket] = useState(1);
    const [validDate, setValidDate] = useState("");

    const [collections, setCollections] = useState<DrinkTicketCollectionDto[]>([]);
    const [selectedCollectionId, setSelectedCollectionId] = useState<number>(0);
    const [tickets, setTickets] = useState<DrinkTicketDto[]>([]);

    const [loadingCollections, setLoadingCollections] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // EVENTS
    useEffect(() => {
        fetch(`${apiUrl}/api/events`)
            .then(r => r.json())
            .then(d => setEvents(toArray(d)))
            .catch(() => setEvents([]));
    }, [apiUrl]);

    // BARS
    const loadBars = async (id: number) => {
        const res = await fetch(`${apiUrl}/api/EventBars/event/${id}`);
        if (!res.ok) return;
        const data = await res.json();
        setBars(toArray(data));
    };

    const loadInvoices = async (id: number) => {
        const res = await fetch(`${apiUrl}/api/invoices/event/${id}`);

        if (!res.ok) return;

        const data = await res.json();

        setInvoices(toArray<InvoiceDto>(data));
    };

    // COLLECTIONS
    const loadCollections = async (id: number) => {
        if (!id) {
            setCollections([]);
            return;
        }

        setLoadingCollections(true);
        setError(null);

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drink-ticket-collections?eventId=${id}`
            );

            if (!res.ok) throw new Error(await res.text());

            const data = await res.json();
            setCollections(toArray<DrinkTicketCollectionDto>(data));
        } catch (e: any) {
            setError("Kunne ikke hente collections");
            setCollections([]);
        } finally {
            setLoadingCollections(false);
        }
    };

    // TICKETS
    const loadTickets = async (id: number) => {
        if (!id) return setTickets([]);

        const res = await fetch(
            `${apiUrl}/api/admin/drink-ticket-collections/${id}/tickets`
        );

        if (!res.ok) return;

        const data = await res.json();
        setTickets(toArray(data));
    };

    const deleteCollection = async (id: number) => {

        if (!window.confirm("Delete this collection and all tickets?"))
            return;

        setError(null);
        setMessage(null);

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drink-ticket-collections/${id}`,
                {
                    method: "DELETE"
                }
            );

            const text = await res.text();

            if (!res.ok) {
                throw new Error(text || "Delete failed");
            }


            setMessage("Collection deleted");

            if (selectedCollectionId === id) {
                setSelectedCollectionId(0);
                setTickets([]);
            }


            await loadCollections(eventId);

        } catch (e: any) {
            setError(e.message);
        }
    };

    // EVENT CHANGE
    useEffect(() => {
        if (!eventId) return;

        setEventBarId(0);
        setInvoiceId(0);
        setSelectedCollectionId(0);
        setTickets([]);

        loadBars(eventId);
        loadInvoices(eventId);
        loadCollections(eventId);

    }, [eventId]);

    // COLLECTION SELECT
    useEffect(() => {
        if (selectedCollectionId) loadTickets(selectedCollectionId);
        else setTickets([]);
    }, [selectedCollectionId]);

    const selectedCollection = useMemo(
        () =>
            collections.find(
                c => c.drinkTicketCollectionId === selectedCollectionId
            ) || null,
        [collections, selectedCollectionId]
    );

    // CREATE
    const submit = async () => {
        if (!eventId || !eventBarId || !invoiceId)
            return setError("Select event + bar + invoice");
        if (!collectionName.trim()) return setError("Missing name");

        setSaving(true);
        setError(null);
        setMessage(null);

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drink-ticket-collections`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        eventId,
                        eventBarId,
                        invoiceId,
                        collectionName,
                        ticketCount,
                        maxDrinksPerTicket,
                        validDate: validDate || null
                    })
                }
            );

            if (!res.ok) throw new Error("Create failed");

            setMessage("Collection created");
            setCollectionName("");
            setValidDate("");

            await loadCollections(eventId);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>

            <HeroSectionV2
                titleEn="Collection"
                titleDa="Collection"
                descriptionEn="Manage Collections For Tickets"
                descriptionDa="Administrer Collections for Tickets"
                imageSrc={Billede1}
            />

            <AdminBackBar
                title="Drinks"
                crumbs={[
                    { label: "Admin", to: "/admin" },
                    { label: "Drinks" }
                ]}
            />


            <Stack p="xl" gap="xl">


                <Paper
                    shadow="sm"
                    radius="md"
                    p="xl"
                    withBorder
                >

                    <Title order={2}>
                        Drink Ticket Collections
                    </Title>

                    <Text c="dimmed">
                        Clean admin management view
                    </Text>

                </Paper>





                {error && (

                    <Alert
                        color="red"
                        title="Error"
                    >
                        {error}
                    </Alert>

                )}



                {message && (

                    <Alert
                        color="green"
                        title="Success"
                    >
                        {message}
                    </Alert>

                )}






                {/* SETUP */}

                <Paper
                    shadow="sm"
                    radius="md"
                    p="xl"
                    withBorder
                >

                    <Stack>

                        <Title order={4}>
                            Setup
                        </Title>


                        <Grid>


                            <Grid.Col span={{ base: 12, md: 4 }}>

                                <Select
                                    label="Event"
                                    value={
                                        eventId
                                            ? String(eventId)
                                            : null
                                    }
                                    onChange={(v) =>
                                        setEventId(toInt(v))
                                    }
                                    data={
                                        events.map(e => ({
                                            value: String(e.eventID),
                                            label:
                                                e.eventName ||
                                                `Event ${e.eventID}`
                                        }))
                                    }
                                    searchable
                                />

                            </Grid.Col>




                            <Grid.Col span={{ base: 12, md: 4 }}>

                                <Select
                                    label="Bar"
                                    value={
                                        eventBarId
                                            ? String(eventBarId)
                                            : null
                                    }
                                    onChange={(v) =>
                                        setEventBarId(toInt(v))
                                    }
                                    data={
                                        bars.map(b => ({
                                            value: String(b.eventBarId),
                                            label: b.name
                                        }))
                                    }
                                />

                            </Grid.Col>




                            <Grid.Col span={{ base: 12, md: 4 }}>

                                <Select
                                    label="Invoice"
                                    value={
                                        invoiceId
                                            ? String(invoiceId)
                                            : null
                                    }
                                    onChange={(v) =>
                                        setInvoiceId(toInt(v))
                                    }
                                    data={
                                        invoices.map(i => ({
                                            value: String(i.invoiceId),
                                            label: i.name
                                        }))
                                    }
                                />

                            </Grid.Col>


                        </Grid>


                    </Stack>


                </Paper>








                {/* CREATE COLLECTION */}


                <Paper
                    shadow="sm"
                    radius="md"
                    p="xl"
                    withBorder
                >

                    <Stack>


                        <Title order={4}>
                            Create Collection
                        </Title>


                        <Grid>


                            <Grid.Col span={{ base: 12, md: 6 }}>

                                <TextInput
                                    label="Name"
                                    value={collectionName}
                                    onChange={(e) =>
                                        setCollectionName(
                                            e.target.value
                                        )
                                    }
                                />

                            </Grid.Col>



                            <Grid.Col span={{ base: 12, md: 2 }}>

                                <NumberInput
                                    label="Tickets"
                                    value={ticketCount}
                                    onChange={(v) =>
                                        setTicketCount(
                                            Number(v)
                                        )
                                    }
                                />

                            </Grid.Col>




                            <Grid.Col span={{ base: 12, md: 2 }}>

                                <NumberInput
                                    label="Max drinks"
                                    value={maxDrinksPerTicket}
                                    onChange={(v) =>
                                        setMaxDrinksPerTicket(
                                            Number(v)
                                        )
                                    }
                                />

                            </Grid.Col>




                            <Grid.Col span={{ base: 12, md: 2 }}>

                                <TextInput
                                    label="Date"
                                    type="date"
                                    value={validDate}
                                    onChange={(e) =>
                                        setValidDate(
                                            e.target.value
                                        )
                                    }
                                />

                            </Grid.Col>


                        </Grid>



                        <Button
                            onClick={submit}
                            loading={saving}
                        >
                            Create Collection
                        </Button>



                    </Stack>


                </Paper>









                {/* COLLECTIONS */}



                <Paper
                    shadow="sm"
                    radius="md"
                    p="xl"
                    withBorder
                >


                    <Group justify="space-between">

                        <Title order={4}>
                            Collections
                        </Title>


                        {
                            loadingCollections &&
                            <Text c="dimmed">
                                Loading...
                            </Text>
                        }


                    </Group>



                    <Divider my="md" />



                    <Stack>


                        {
                            !loadingCollections &&
                            collections.length === 0 &&

                            <Text c="dimmed">
                                No collections found
                            </Text>
                        }



                        {
                            collections.map(c => (

                                <Paper
                                    key={c.drinkTicketCollectionId}
                                    p="md"
                                    radius="md"
                                    withBorder
                                    style={{
                                        cursor: "pointer",
                                        border:
                                            selectedCollectionId === c.drinkTicketCollectionId
                                                ? "2px solid black"
                                                : undefined
                                    }}
                                    onClick={() =>
                                        setSelectedCollectionId(
                                            c.drinkTicketCollectionId
                                        )
                                    }
                                >


                                    <Group justify="space-between">


                                        <Stack gap={5}>


                                            <Text fw={600}>
                                                {c.collectionName}
                                            </Text>


                                            <Text size="sm" c="dimmed">

                                                {c.ticketCount} tickets ·{" "}
                                                {formatDate(c.validDate)}

                                            </Text>


                                        </Stack>



                                        <Button
                                            color="red"
                                            onClick={(e) => {

                                                e.stopPropagation();

                                                deleteCollection(
                                                    c.drinkTicketCollectionId
                                                );

                                            }}
                                        >
                                            Delete
                                        </Button>



                                    </Group>


                                </Paper>


                            ))
                        }


                    </Stack>


                </Paper>









                {/* TICKETS */}



                {
                    selectedCollection &&

                    <Paper
                        shadow="sm"
                        radius="md"
                        p="xl"
                        withBorder
                    >

                        <Title order={4} mb="md">
                            Tickets
                        </Title>


                        <Table
                            striped
                            highlightOnHover
                            withTableBorder
                        >


                            <Table.Thead>

                                <Table.Tr>

                                    <Table.Th>
                                        Code
                                    </Table.Th>


                                    <Table.Th>
                                        #
                                    </Table.Th>


                                    <Table.Th>
                                        Drinks
                                    </Table.Th>


                                    <Table.Th>
                                        Status
                                    </Table.Th>


                                </Table.Tr>


                            </Table.Thead>




                            <Table.Tbody>


                                {
                                    tickets.map(t => (

                                        <Table.Tr
                                            key={t.drinkTicketId}
                                        >

                                            <Table.Td>
                                                {t.ticketCode}
                                            </Table.Td>


                                            <Table.Td>
                                                {t.ticketNumber}
                                            </Table.Td>


                                            <Table.Td>
                                                {t.maxDrinks}
                                            </Table.Td>


                                            <Table.Td>

                                                <Badge
                                                    color={
                                                        t.isActive
                                                            ? "green"
                                                            : "gray"
                                                    }
                                                >
                                                    {
                                                        t.isActive
                                                            ? "Active"
                                                            : "Used"
                                                    }
                                                </Badge>

                                            </Table.Td>


                                        </Table.Tr>


                                    ))
                                }


                            </Table.Tbody>


                        </Table>


                    </Paper>

                }



            </Stack>


        </div>
    );
};

export default AdminDrinkTicketCollections;